import React, { lazy, useEffect } from 'react';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';

import Connector, { PropsFromRedux } from './Connector';
import { Wrapper } from './styled';
import EventsSummaryBars from './components/EventsSummaryBars';
import { useParams } from 'react-router-dom';
import { linkParamsType } from 'src/routes/MedicalReports/types';
import { deepEqual } from 'src/utils/comparators';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  getFormatDateByContinent,
  MEDICAL_REPORT_DATE_FORMAT,
  MONTH_DAY_YEAR_FORMAT,
} from 'src/utils/timeUtils';
import FooterComponent from 'src/routes/MedicalReports/components/FooterComponent';
import RenderOnViewportEntry from 'src/routes/MedicalReports/components/RenderOnViewportEntry';

const HeaderComponent = lazy(() => import('./components/HeaderComponent'));
const LastDayAbnormalityTable = lazy(
  () => import('./components/LastDayAbnormalityTable'),
);
const Last3DaysAbnormalityTable = lazy(
  () => import('./components/Last3DaysAbnormalityTable'),
);
const DevicesPieChart = lazy(() => import('./components/DevicesPieChart'));

dayjs.extend(customParseFormat);

type Props = PropsFromRedux & { intl: IntlShape };
const MedicalReportPage = ({
  intl,
  getAvailableFeatures,
}: Props): JSX.Element => {
  const { tenantId, date } = useParams<linkParamsType>();
  const reportDate = dayjs(date, MEDICAL_REPORT_DATE_FORMAT);
  const abnormality72hEndDate = reportDate
    .subtract(1, 'day')
    .format(getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT));
  const abnormality72hStartDate = reportDate
    .subtract(3, 'day')
    .format(getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT));

  useEffect(() => {
    getAvailableFeatures();
  }, []);

  return (
    <Wrapper>
      <RenderOnViewportEntry style={{ minHeight: '90px' }}>
        <HeaderComponent />
      </RenderOnViewportEntry>
      <RenderOnViewportEntry style={{ minHeight: '760px' }}>
        <EventsSummaryBars
          tenantId={tenantId}
          date={date}
          title={intl.formatMessage(messages.title)}
        />
      </RenderOnViewportEntry>
      <RenderOnViewportEntry>
        <LastDayAbnormalityTable
          tenantId={tenantId}
          date={date}
          title={intl.formatMessage(messages.abnormalityTable24HoursTitle, {
            date: reportDate.format(
              getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT),
            ),
          })}
          locale={{
            emptyText: intl.formatMessage(messages.noLastDayAbnormalities),
          }}
        />
      </RenderOnViewportEntry>
      <RenderOnViewportEntry>
        <Last3DaysAbnormalityTable
          tenantId={tenantId}
          date={date}
          locale={{
            emptyText: intl.formatMessage(messages.no3dAbnormalities),
          }}
          title={intl.formatMessage(messages.abnormalityTable3DaysTitle, {
            startDate: abnormality72hStartDate,
            endDate: abnormality72hEndDate,
          })}
        />
      </RenderOnViewportEntry>
      <RenderOnViewportEntry style={{ minHeight: '1770px' }}>
        <DevicesPieChart
          title={intl.formatMessage(messages.operationalSummaryTitle)}
          tenantId={tenantId}
          date={date}
          showDevicesConsent
        />
      </RenderOnViewportEntry>
      <FooterComponent />
    </Wrapper>
  );
};

const messages = defineMessages({
  title: {
    id: 'MedicalReports.Component.title',
    defaultMessage: 'Summary',
  },
  abnormalityTable24HoursTitle: {
    id: 'MedicalReports.Component.abnormalityTable24HoursTitle',
    defaultMessage: 'Abnormality Indications over prior 24 hours ({date})',
  },
  noLastDayAbnormalities: {
    id: 'MedicalReports.Component.noLastDayAbnormalities',
    defaultMessage: 'No abnormalities detected in the past 24 hours.',
  },
  no3dAbnormalities: {
    id: 'MedicalReports.Component.no3dAbnormalities',
    defaultMessage: 'No abnormalities detected in the past 72 hours.',
  },
  abnormalityTable3DaysTitle: {
    id: 'MedicalReports.Component.abnormalityTable3DaysTitle',
    defaultMessage:
      'Abnormality Indications over prior 72 hours ({startDate} -{endDate})',
  },
  operationalSummaryTitle: {
    id: 'MedicalReports.Component.operationalSummaryTitle',
    defaultMessage: 'Operational Summary',
  },
});

export default Connector(
  injectIntl(
    React.memo(MedicalReportPage, (oldProps, newProps) =>
      deepEqual(oldProps, newProps),
    ),
  ),
);
